import { FC, ReactNode } from 'react';

import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Text } from '../common/Text';
import {
  WhySgSecurityBadgeAiPoweredIcon,
  WhySgSecurityBadgeCertifiedIcon,
  WhySgSecurityBadge3dsFingerprintIcon,
  WhySgSecurityBadgeSslLockIcon,
  WhySgSecurityBannerMetricLockIcon,
  WhySgSecurityBannerMetricMonitoredIcon,
  WhySgSecurityBannerMetricSecuredIcon,
  WhySgSecurityBannerMetricShieldIcon,
  WhySgSecurityCheckMiniIcon,
  WhySgSecurityGlobeStatIcon,
  WhySgSecurityShieldAlertStatIcon,
  WhySgSecurityStatPciMedalIcon,
  WhySgSecurityTechnologyCardIcon,
} from '../whySg/icons/WhySgSecurityBlockIcons';
import { WhySgShieldCheckSecurityCardIcon, WhySgShieldCheckUptimeStatIcon } from '../whySg/icons/WhySgShieldCheckIcons';

import styles from './WhySgSecurityBlock.module.scss';

function ListItem(props: { children: ReactNode }) {
  const { children } = props;

  return (
    <li className={styles.listItem}>
      <WhySgSecurityCheckMiniIcon className={styles.checkIcon} />

      <Text type={'regular14'} color={'white'} whiteSpace={'normal'}>
        {children}
      </Text>
    </li>
  );
}

export const WhySgSecurityBlock: FC = () => {
  return (
    <Container className={styles.root}>
      <div className={styles.layout}>
        <div className={styles.sectionLabel}>
          <span className={styles.sectionLabelLine} aria-hidden />

          <Text type={'medium14'} color={'green'} className={styles.sectionEyebrow}>
            Security
          </Text>
        </div>

        <Flex direction={'column'} gap={6} className={styles.intro}>
          <Text type={'regular48'} color={'white'} align={'center'} className={styles.title}>
            Enterprise-Grade Security
          </Text>

          <Text
            type={'regular16'}
            color={'white50'}
            whiteSpace={'normal'}
            align={'center'}
            className={styles.description}
          >
            Comprehensive compliance and data protection built into every layer of our platform. We safeguard your
            transactions with industry-leading security standards and cutting-edge technology.
          </Text>
        </Flex>

        <div className={styles.featureGrid}>
          <article className={styles.card}>
            <div className={styles.cardInner}>
              <Flex alignItems={'center'} gap={4}>
                <Flex className={styles.iconWrap} aria-hidden alignItems={'center'} justifyContent={'center'}>
                  <WhySgShieldCheckSecurityCardIcon />
                </Flex>

                <Flex direction={'column'} gap={2}>
                  <Text type={'medium14'} className={styles.categoryLabel}>
                    Compliance
                  </Text>

                  <Text type={'h3'} color={'white'}>
                    PCI DSS Level 1
                  </Text>
                </Flex>
              </Flex>

              <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
                {`Full PCI DSS Level 1 certification — the highest level of payment security compliance. Your customers' sensitive data is protected by the same standards used by the world's largest financial institutions.`}
              </Text>

              <ul className={styles.list}>
                <ListItem>Annual on-site security audits</ListItem>
                <ListItem>End-to-end encryption (E2EE)</ListItem>
                <ListItem>Tokenization of card data</ListItem>
                <ListItem>SOC 2 Type II compliant</ListItem>
              </ul>

              <div className={styles.badgesRow}>
                <span className={styles.badgeAccent}>
                  <WhySgSecurityBadgeCertifiedIcon aria-hidden />
                  Certified
                </span>

                <span className={styles.badgeMuted}>
                  <WhySgSecurityBadgeSslLockIcon aria-hidden />
                  256-bit SSL
                </span>
              </div>
            </div>
          </article>

          <article className={styles.card}>
            <div className={styles.cardInner}>
              <Flex alignItems={'center'} gap={4}>
                <Flex className={styles.iconWrap} aria-hidden alignItems={'center'} justifyContent={'center'}>
                  <WhySgSecurityTechnologyCardIcon />
                </Flex>

                <Flex direction={'column'} gap={2}>
                  <Text type={'medium14'} className={styles.categoryLabel}>
                    Technology
                  </Text>

                  <Text type={'h3'} color={'white'}>
                    AI Anti-Fraud Suite
                  </Text>
                </Flex>
              </Flex>

              <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
                AI-powered anti-fraud suite and 3D-Secure 2.0 to significantly reduce chargeback risks. Our machine
                learning models analyze thousands of signals in real-time to detect and prevent fraudulent transactions.
              </Text>

              <ul className={styles.list}>
                <ListItem>Real-time fraud scoring</ListItem>
                <ListItem>3D-Secure 2.0 integration</ListItem>
                <ListItem>Adaptive risk engine</ListItem>
                <ListItem>Chargeback reduction up to 70%</ListItem>
              </ul>

              <div className={styles.badgesRow}>
                <span className={styles.badgeAccent}>
                  <WhySgSecurityBadgeAiPoweredIcon aria-hidden />
                  AI-Powered
                </span>

                <span className={styles.badgeMuted}>
                  <WhySgSecurityBadge3dsFingerprintIcon aria-hidden />
                  3DS 2.0
                </span>
              </div>
            </div>
          </article>
        </div>

        <div className={styles.banner}>
          <div className={styles.bannerInner}>
            <Flex direction={'column'} gap={2} className={styles.bannerText}>
              <Text type={'h2'} color={'white'}>
                Trusted by 10,000+ Merchants Worldwide
              </Text>

              <Text type={'regular16'} color={'white50'} whiteSpace={'normal'} className={styles.bannerSubtitle}>
                Processing billions in secure transactions every year
              </Text>
            </Flex>

            <div className={styles.bannerMetrics}>
              {(
                [
                  { Icon: WhySgSecurityBannerMetricShieldIcon, label: 'Protected' },
                  { Icon: WhySgSecurityBannerMetricLockIcon, label: 'Encrypted' },
                  { Icon: WhySgSecurityBannerMetricSecuredIcon, label: 'Secured' },
                  { Icon: WhySgSecurityBannerMetricMonitoredIcon, label: 'Monitored' },
                ] as const
              ).map(({ Icon, label }) => (
                <div key={label} className={styles.metricItem}>
                  <div className={styles.metricIcon} aria-hidden>
                    <Icon />
                  </div>

                  <Text type={'regular12'} className={styles.metricLabel}>
                    {label}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.statsGrid}>
          {(
            [
              { Icon: WhySgShieldCheckUptimeStatIcon, value: '99.99%', label: 'Uptime SLA' },
              { Icon: WhySgSecurityShieldAlertStatIcon, value: '0.01%', label: 'Fraud Rate' },
              { Icon: WhySgSecurityStatPciMedalIcon, value: 'Level 1', label: 'PCI DSS' },
              { Icon: WhySgSecurityGlobeStatIcon, value: '150+', label: 'Countries' },
            ] as const
          ).map(({ Icon, value, label }) => (
            <article key={label} className={styles.statCard}>
              <div className={styles.statCardInner}>
                <div className={styles.statIcon} aria-hidden>
                  <Icon />
                </div>

                <span className={styles.statValue}>{value}</span>

                <Text
                  type={'regular12'}
                  color={'white50'}
                  align={'center'}
                  whiteSpace={'normal'}
                  className={styles.statCaption}
                >
                  {label}
                </Text>
              </div>
            </article>
          ))}
        </div>
      </div>
    </Container>
  );
};
